import { useState } from 'react';
import { Link } from 'react-router-dom';
import useScrollToTop from '../hooks/useScrollToTop';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const SignupPage = () => {
  // Scroll to top when route changes
  useScrollToTop();

  // Get translations
  const { language } = useLanguage();
  const t = translations[language];

  const [formState, setFormState] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    password: '',
    confirmPassword: '',
    phone: '',
    jobTitle: '',
    companySize: '',
    plan: 'professional',
    acceptTerms: false
  });
  
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when field is updated
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formState.firstName.trim()) {
      newErrors.firstName = t.signupPage.firstNameRequired;
    }
    
    if (!formState.lastName.trim()) {
      newErrors.lastName = t.signupPage.lastNameRequired;
    }
    
    if (!formState.email.trim()) {
      newErrors.email = t.signupPage.emailRequired;
    } else if (!/\S+@\S+\.\S+/.test(formState.email)) {
      newErrors.email = t.signupPage.emailInvalid;
    }
    
    if (!formState.company.trim()) {
      newErrors.company = t.signupPage.companyRequired;
    }
    
    if (!formState.password) {
      newErrors.password = t.signupPage.passwordRequired;
    } else if (formState.password.length < 8) {
      newErrors.password = t.signupPage.passwordMinLength;
    }
    
    if (formState.password !== formState.confirmPassword) {
      newErrors.confirmPassword = t.signupPage.passwordsDoNotMatch;
    }
    
    if (!formState.acceptTerms) {
      newErrors.acceptTerms = t.signupPage.termsRequired;
    }
    
    return newErrors;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    const formErrors = validateForm();
    
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    // In a real implementation, you would submit the form data to your backend
    console.log('Form submitted:', formState);
    setSubmitted(true);
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-800 to-indigo-900 text-white py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-bold mb-6">{t.signupPage.heroTitle}</h1>
            <p className="text-xl mb-8 text-blue-100">
              {t.signupPage.heroSubtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row shadow-lg rounded-lg overflow-hidden">
              {/* Registration Form */}
              <div className="lg:w-2/3 bg-white p-8 md:p-12">
                {submitted ? (
                  <div className="text-center py-16">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-500 mb-6">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-4">{t.signupPage.thankYou}</h3>
                    <p className="text-gray-600 mb-8">
                      {t.signupPage.confirmationSent} <strong>{formState.email}</strong>. {t.signupPage.checkInbox}
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                      <Link 
                        to="/" 
                        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition duration-200"
                      >
                        {t.signupPage.returnToHome}
                      </Link>
                    </div>
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-gray-800 mb-6">{t.signupPage.createAccount}</h2>
                    <form onSubmit={handleSubmit}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        <div>
                          <label htmlFor="firstName" className="block text-gray-700 font-medium mb-2">{t.signupPage.firstName} *</label>
                          <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={formState.firstName}
                            onChange={handleChange}
                            className={`w-full px-4 py-3 rounded-lg border ${errors.firstName ? 'border-red-500' : 'border-gray-300'} focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition`}
                          />
                          {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                        </div>
                        <div>
                          <label htmlFor="lastName" className="block text-gray-700 font-medium mb-2">{t.signupPage.lastName} *</label>
                          <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={formState.lastName}
                            onChange={handleChange}
                            className={`w-full px-4 py-3 rounded-lg border ${errors.lastName ? 'border-red-500' : 'border-gray-300'} focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition`}
                          />
                          {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        <div>
                          <label htmlFor="email" className="block text-gray-700 font-medium mb-2">{t.signupPage.workEmail} *</label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formState.email}
                            onChange={handleChange}
                            className={`w-full px-4 py-3 rounded-lg border ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition`}
                          />
                          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                        </div>
                        <div>
                          <label htmlFor="company" className="block text-gray-700 font-medium mb-2">{t.signupPage.companyName} *</label>
                          <input
                            type="text"
                            id="company"
                            name="company"
                            value={formState.company}
                            onChange={handleChange}
                            className={`w-full px-4 py-3 rounded-lg border ${errors.company ? 'border-red-500' : 'border-gray-300'} focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition`}
                          />
                          {errors.company && <p className="text-red-500 text-sm mt-1">{errors.company}</p>}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        <div>
                          <label htmlFor="password" className="block text-gray-700 font-medium mb-2">{t.signupPage.password} *</label>
                          <input
                            type="password"
                            id="password"
                            name="password"
                            value={formState.password}
                            onChange={handleChange}
                            className={`w-full px-4 py-3 rounded-lg border ${errors.password ? 'border-red-500' : 'border-gray-300'} focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition`}
                          />
                          {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                          <p className="text-gray-500 text-sm mt-1">{t.signupPage.mustBeAtLeast8Chars}</p>
                        </div>
                        <div>
                          <label htmlFor="confirmPassword" className="block text-gray-700 font-medium mb-2">{t.signupPage.confirmPassword} *</label>
                          <input
                            type="password"
                            id="confirmPassword"
                            name="confirmPassword"
                            value={formState.confirmPassword}
                            onChange={handleChange}
                            className={`w-full px-4 py-3 rounded-lg border ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'} focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition`}
                          />
                          {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        <div>
                          <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">{t.signupPage.phoneNumber}</label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formState.phone}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                          />
                        </div>
                        <div>
                          <label htmlFor="jobTitle" className="block text-gray-700 font-medium mb-2">{t.signupPage.jobTitle}</label>
                          <input
                            type="text"
                            id="jobTitle"
                            name="jobTitle"
                            value={formState.jobTitle}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                          />
                        </div>
                      </div>
                      
                      <div className="mb-8">
                        <label htmlFor="companySize" className="block text-gray-700 font-medium mb-2">{t.signupPage.companySize}</label>
                        <select
                          id="companySize"
                          name="companySize"
                          value={formState.companySize}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                        >
                          <option value="">{t.signupPage.selectOption}</option>
                          <option value="1-10">{t.signupPage.employees1to10}</option>
                          <option value="11-50">{t.signupPage.employees11to50}</option>
                          <option value="51-200">{t.signupPage.employees51to200}</option>
                          <option value="201-500">{t.signupPage.employees201to500}</option>
                          <option value="501-1000">{t.signupPage.employees501to1000}</option>
                          <option value="1000+">{t.signupPage.employees1000plus}</option>
                        </select>
                      </div>
                      
                      <div className="mb-8">
                        <label className="block text-gray-700 font-medium mb-4">{t.signupPage.selectTrialPlan} *</label>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <PlanOption 
                            id="starter"
                            name="plan"
                            value="starter"
                            title={t.starter}
                            price="€99"
                            isSelected={formState.plan === 'starter'}
                            onChange={handleChange}
                          />
                          <PlanOption 
                            id="business"
                            name="plan"
                            value="business"
                            title={t.business}
                            price="€249"
                            isSelected={formState.plan === 'business'}
                            onChange={handleChange}
                            isRecommended={true}
                            recommendedText={t.signupPage.recommended}
                          />
                          <PlanOption 
                            id="enterprise"
                            name="plan"
                            value="enterprise"
                            title={t.enterprise}
                            price={t.custom}
                            isSelected={formState.plan === 'enterprise'}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      
                      <div className="mb-8">
                        <div className={`flex items-start ${errors.acceptTerms ? 'text-red-500' : 'text-gray-700'}`}>
                          <div className="flex items-center h-5">
                            <input
                              type="checkbox"
                              id="acceptTerms"
                              name="acceptTerms"
                              checked={formState.acceptTerms}
                              onChange={handleChange}
                              className="w-4 h-4 border border-gray-300 rounded focus:ring-3 focus:ring-blue-300"
                            />
                          </div>
                          <label htmlFor="acceptTerms" className="ml-2 text-sm font-medium">
                            {t.signupPage.agreeToTerms} <Link to="/terms" className="text-blue-600 hover:underline">{t.signupPage.termsOfService}</Link> {t.signupPage.and} <Link to="/privacy" className="text-blue-600 hover:underline">{t.signupPage.privacyPolicy}</Link> *
                          </label>
                        </div>
                        {errors.acceptTerms && <p className="text-red-500 text-sm mt-1">{errors.acceptTerms}</p>}
                      </div>
                      
                      <button 
                        type="submit"
                        className="w-full py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition duration-200"
                      >
                        {t.signupPage.createAccountAndStartTrial}
                      </button>
                      
                      <div className="mt-6 text-center text-gray-600">
                        {t.signupPage.alreadyHaveAccount} <Link to="/login" className="text-blue-600 hover:underline">{t.signupPage.logIn}</Link>
                      </div>
                    </form>
                  </>
                )}
              </div>
              
              {/* Trial Info */}
              <div className="lg:w-1/3 bg-blue-50 p-8 md:p-12">
                <h3 className="text-xl font-bold text-gray-800 mb-6">{t.signupPage.trialIncludes}</h3>
                <ul className="space-y-4 mb-8">
                  <TrialFeature text={t.signupPage.fullAccess} />
                  <TrialFeature text={t.signupPage.unlimitedScreenings} />
                  <TrialFeature text={t.signupPage.completeWatchlists} />
                  <TrialFeature text={t.signupPage.apiIntegration} />
                  <TrialFeature text={t.signupPage.expertSupport} />
                  <TrialFeature text={t.signupPage.comprehensiveDocs} />
                </ul>
                
                <div className="bg-white rounded-lg p-6 shadow-sm mb-8">
                  <h4 className="font-bold text-gray-800 mb-2">{t.signupPage.noCommitment}</h4>
                  <p className="text-gray-600 text-sm">
                    {t.signupPage.noCommitmentDesc}
                  </p>
                </div>
                
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h4 className="font-bold text-gray-800 mb-2">{t.signupPage.needHelp}</h4>
                  <p className="text-gray-600 text-sm mb-4">
                    {t.signupPage.needHelpDesc}
                  </p>
                  <a 
                    href={`mailto:${t.signupPage.salesEmail}`}
                    className="text-blue-600 hover:underline text-sm font-medium flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {t.signupPage.salesEmail}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const PlanOption = ({ id, name, value, title, price, isSelected, onChange, isRecommended, recommendedText }) => {
  const { language } = useLanguage();
  const t = translations[language];
  
  return (
    <div className={`relative border rounded-lg p-4 ${isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'}`}>
      {isRecommended && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white text-xs py-1 px-3 rounded-full">
          {recommendedText}
        </div>
      )}
      <div className="flex items-center mb-4">
        <input 
          type="radio" 
          id={id} 
          name={name} 
          value={value}
          checked={isSelected}
          onChange={onChange}
          className="w-4 h-4 text-blue-600 focus:ring-blue-500"
        />
        <label htmlFor={id} className="ml-2 text-lg font-medium text-gray-800">{title}</label>
      </div>
      <div className="pl-6">
        <div className="text-2xl font-bold text-gray-800 mb-1">{price}</div>
        <div className="text-gray-500 text-sm">{t.signupPage.perMonth}</div>
      </div>
    </div>
  );
};

const TrialFeature = ({ text }) => {
  return (
    <li className="flex items-center">
      <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
      <span className="text-gray-700">{text}</span>
    </li>
  );
};

export default SignupPage; 